import React, { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "@/components/ui/sonner";
import { CheckCircle, XCircle, Clock } from "@phosphor-icons/react";

function formatRelativeAgo(msTs) {
    const diff = Date.now() - msTs;
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `${mins} min ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs} hr${hrs === 1 ? "" : "s"} ago`;
    const days = Math.floor(hrs / 24);
    return `${days} day${days === 1 ? "" : "s"} ago`;
}

/**
 * InterviewAttendanceToggle — V1.5.6.4.
 * Renders a tri-state toggle (Attended / No-show / Pending) below each
 * scheduled interview inside the candidate drawer. Recruiters flip it
 * after the interview end-time; the "attended" boolean feeds into the
 * recruiter-performance report's `interviews_attended` metric.
 */
export default function InterviewAttendanceToggle({ candidateId, interview, onUpdated }) {
    const current = interview?.attended ?? null; // true | false | null (Pending)
    const [saving, setSaving] = useState(false);
    const set = async (val) => {
        if (val === current) return;
        setSaving(true);
        try {
            await api.patch(
                `/candidates/${candidateId}/interviews/${interview.id}/attended`,
                { attended: val },
            );
            toast.success(
                val === true ? "Marked as attended"
                : val === false ? "Marked as no-show"
                : "Reset to pending",
            );
            onUpdated?.();
        } catch (ex) {
            toast.error(formatApiError(ex.response?.data?.detail) || ex.message);
        } finally {
            setSaving(false);
        }
    };

    const now = Date.now();
    const end = interview?.end ? new Date(interview.end).getTime() : null;
    const isFuture = end && end > now;
    // V1.5.7.1 — Nudge: once 30 min have passed since interview end AND we
    // haven't marked attended yet, we surface a prominent amber banner so
    // recruiters can't miss it. Auto-dismisses when they click Attended/No-show.
    const NUDGE_MIN_MS = 30 * 60 * 1000;
    const isOverdue =
        current === null && end !== null && now > (end + NUDGE_MIN_MS);

    return (
        <div
            data-testid={`interview-attendance-${interview.id}`}
            className="mt-3 pt-2 border-t border-[#F5F5F5]"
        >
            {isOverdue && (
                <div
                    data-testid={`interview-attendance-nudge-${interview.id}`}
                    className="mb-2 border-l-4 border-amber-500 bg-amber-50 px-3 py-2 text-xs text-amber-900"
                >
                    <p className="font-black uppercase tracking-wider text-[10px] mb-0.5">
                        Confirm attendance
                    </p>
                    <p>
                        This interview ended {formatRelativeAgo(end)}. Please confirm
                        whether the candidate turned up — it feeds the recruiter performance
                        report and (with a Rejected assessment) can advance the funnel.
                    </p>
                </div>
            )}
            <p className="text-[10px] font-bold uppercase tracking-wider text-[#525252] mb-1.5">
                {isFuture ? "Attendance (pending — mark after interview)" : "Did the candidate attend?"}
            </p>
            <div className="flex items-center gap-1.5">
                <button
                    type="button"
                    data-testid={`interview-attended-yes-${interview.id}`}
                    disabled={saving}
                    onClick={() => set(true)}
                    className={`inline-flex items-center gap-1 px-2 py-1 text-[10px] font-bold uppercase tracking-wider border transition-colors ${
                        current === true
                            ? "bg-emerald-600 border-emerald-600 text-white"
                            : "bg-white border-[#E5E5E5] text-[#525252] hover:border-emerald-600 hover:text-emerald-700"
                    } disabled:opacity-60`}
                >
                    <CheckCircle size={10} weight="fill" /> Attended
                </button>
                <button
                    type="button"
                    data-testid={`interview-attended-no-${interview.id}`}
                    disabled={saving}
                    onClick={() => set(false)}
                    className={`inline-flex items-center gap-1 px-2 py-1 text-[10px] font-bold uppercase tracking-wider border transition-colors ${
                        current === false
                            ? "bg-[#DC2626] border-[#DC2626] text-white"
                            : "bg-white border-[#E5E5E5] text-[#525252] hover:border-[#DC2626] hover:text-[#DC2626]"
                    } disabled:opacity-60`}
                >
                    <XCircle size={10} weight="fill" /> No-show
                </button>
                {current !== null && (
                    <button
                        type="button"
                        data-testid={`interview-attended-reset-${interview.id}`}
                        disabled={saving}
                        onClick={() => set(null)}
                        className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-bold uppercase tracking-wider border border-[#E5E5E5] text-[#A3A3A3] hover:text-ink"
                    >
                        <Clock size={10} weight="bold" /> Reset
                    </button>
                )}
            </div>
            {interview.attended_by_user_name && current !== null && (
                <p className="text-[10px] text-[#A3A3A3] mt-1">
                    By {interview.attended_by_user_name}
                    {interview.attended_at ? ` · ${new Date(interview.attended_at).toLocaleString()}` : ""}
                </p>
            )}
        </div>
    );
}
